import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

// Translation interfaces
export interface TranslationData {
  [key: string]: any;
}

export interface SupportedLocale {
  code: string;
  name: string;
  flag: string;
}

// Supported locales configuration
export const SUPPORTED_LOCALES: SupportedLocale[] = [
  { code: 'en-us', name: 'English (US)', flag: '🇺🇸' },
  { code: 'fr-ca', name: 'Français (Canada)', flag: '🇨🇦' }
];

@Injectable({
  providedIn: 'root'
})
export class TranslationService {
  private currentLocale$ = new BehaviorSubject<string>('en-us');
  private translations: Map<string, TranslationData> = new Map();
  private loadedLocales = new Set<string>();

  constructor() {
    // Initialize with default locale
    this.initializeDefaultLocale();
  }

  /**
   * Get the current locale as observable
   */
  getCurrentLocale(): Observable<string> {
    return this.currentLocale$.asObservable();
  }

  /**
   * Get the current locale value
   */
  getCurrentLocaleValue(): string {
    return this.currentLocale$.value;
  }

  /**
   * Set the current locale and load translations if needed
   */
  async setLocale(locale: string): Promise<void> {
    if (!this.isLocaleSupported(locale)) {
      console.warn(`Locale ${locale} is not supported. Using en-us as fallback.`);
      locale = 'en-us';
    }

    // Load translations if not already loaded
    if (!this.loadedLocales.has(locale)) {
      await this.loadTranslations(locale);
    }

    this.currentLocale$.next(locale);
  }

  /**
   * Get a translated string by key path (e.g., 'experience.congratulations')
   */
  translate(key: string, params?: { [key: string]: any }): string {
    const currentLocale = this.currentLocale$.value;
    const translations = this.translations.get(currentLocale);
    
    if (!translations) {
      console.warn(`No translations loaded for locale: ${currentLocale}`);
      return key;
    }

    const translation = this.getNestedValue(translations, key);
    
    if (!translation) {
      // Try fallback to English
      const fallbackTranslations = this.translations.get('en-us');
      if (fallbackTranslations) {
        const fallbackTranslation = this.getNestedValue(fallbackTranslations, key);
        if (fallbackTranslation) {
          return this.interpolateParams(fallbackTranslation, params);
        }
      }
      
      console.warn(`Translation not found for key: ${key} in locale: ${currentLocale}`);
      return key;
    }

    return this.interpolateParams(translation, params);
  }

  /**
   * Get all supported locales
   */
  getSupportedLocales(): SupportedLocale[] {
    return SUPPORTED_LOCALES;
  }

  /**
   * Check if a locale is supported
   */
  isLocaleSupported(locale: string): boolean {
    return SUPPORTED_LOCALES.some(l => l.code === locale);
  }

  /**
   * Determine locale based on client settings
   * This will be integrated with the client context service
   */
  determineClientLocale(clientSettings?: any): string {
    // For Loto-Quebec, default to French-Canadian
    if (clientSettings?.clientId === 'loto-quebec' || 
        clientSettings?.clientName?.toLowerCase().includes('loto')) {
      return 'fr-ca';
    }

    // Use client's locale setting if available
    if (clientSettings?.locale && this.isLocaleSupported(clientSettings.locale)) {
      return clientSettings.locale;
    }

    // Default fallback
    return 'en-us';
  }

  /**
   * Load translations for a specific locale
   */
  private async loadTranslations(locale: string): Promise<void> {
    try {
      // In a real implementation, this would load from JSON files
      // For now, we'll import them as TypeScript modules
      let translations: TranslationData;
      
      switch (locale) {
        case 'fr-ca':
          translations = await this.getFrenchCanadianTranslations();
          break;
        case 'en-us':
        default:
          translations = await this.getEnglishTranslations();
          break;
      }

      this.translations.set(locale, translations);
      this.loadedLocales.add(locale);
    } catch (error) {
      console.error(`Failed to load translations for locale: ${locale}`, error);
      throw error;
    }
  }

  /**
   * Get English translations
   */
  private async getEnglishTranslations(): Promise<TranslationData> {
    return {
      "experience": {
        "congratulations": "Congratulations!",
        "youHaveWon": "You have won:",
        "pleaseEnterEmail": "Please enter your email to claim your prize.",
        "emailOrPhone": "Phone number or email",
        "invalidEmailOrPhone": "Please enter a valid phone number or email address.",
        "smsConsent": "By checking this box and clicking 'Submit', you agree to receive SMS communications from Blackfire Innovation. You can unsubscribe at any time by replying STOP. For more information, please see our",
        "smsOnlyForPhone": "SMS communications are only available for phone numbers.",
        "privacyPolicy": "Privacy Policy",
        "submit": "Submit",
        "claimPrize": "Claim Prize",
        "areYouReady": "Are you ready to win?",
        "cameraPermissionIntro": "In order to enjoy this AR experience, please allow camera permissions on the following screen.",
        "ready": "Ready",
        "termsAcknowledgment": "By clicking \"Ready\", you are acknowledging that you are over 21 years old and agree to the",
        "termsOfService": "Terms of Service",
        "and": "and",
        "ofBlackfire": "of Blackfire Innovation, Inc.",
        "spin": "Spin",
        "keepCodeInFrame": "Please keep the code in frame",
        "rotateDevice": "Please rotate your device to portrait mode",
        "checkingCameraPermissions": "Checking camera permissions...",
        "allowCameraPermissions": "Please allow camera permissions.",
        "unableToAccessCamera": "Unable to access your camera.",
        "cameraDeniedPermissions": "You have denied camera permissions.",
        "unableToStartCamera": "Unable to start your camera.",
        "proceedToLoyaltyDesk": "Please proceed to the loyalty club desk to redeem your prize.",
        "loyaltyRepresentative": "Once you are in front of a loyalty representative, press the redeem button to collect your prize.",
        "redeem": "Redeem",
        "prizeWon": "Prize won:",
        "redemptionCode": "Redemption code:",
        "thankYouForPlaying": "Thank you for playing!",
        "loading": "Loading...",
        "error": "Error",
        "tryAgain": "Try Again"
      },
      "campaignError": {
        "invalidLink": "Invalid Link",
        "campaignNotFound": "Campaign Not Found",
        "campaignExpired": "Campaign Expired",
        "campaignNotStarted": "Campaign Not Started",
        "campaignUnavailable": "Campaign Unavailable",
        "securityError": "Security Error",
        "somethingWentWrong": "Something Went Wrong",
        "campaignCode": "Campaign Code:",
        "whatYouCanDo": "What you can do:",
        "goToHomepage": "Go to Homepage",
        "contactSupport": "Contact Support",
        "needImmediateAssistance": "Need immediate assistance? Contact the venue directly or scan a new QR code.",
        "clientMismatchMessage": "This campaign belongs to a different client ({{foundClient}}) and cannot be accessed from this app instance (configured for {{expectedClient}}). This is a security protection to prevent cross-client data access.",
        "suggestedActions": {
          "contactVenue": "Contact the venue that provided this QR code",
          "verifyCorrectCode": "Verify you are scanning the correct QR code for this location",
          "ownerContactSupport": "If you are the venue owner, contact support for configuration assistance"
        }
      },
      "common": {
        "loading": "Loading...",
        "error": "Error",
        "success": "Success",
        "close": "Close",
        "cancel": "Cancel",
        "ok": "OK",
        "yes": "Yes",
        "no": "No"
      }
    };
  }

  /**
   * Get French Canadian translations
   */
  private async getFrenchCanadianTranslations(): Promise<TranslationData> {
    return {
      "experience": {
        "congratulations": "Félicitations!",
        "youHaveWon": "Vous avez gagné:",
        "pleaseEnterEmail": "Veuillez entrer votre courriel pour réclamer votre prix.",
        "emailOrPhone": "Numéro de téléphone ou courriel",
        "invalidEmailOrPhone": "Veuillez entrer un numéro de téléphone ou une adresse courriel valide.",
        "smsConsent": "En cochant cette case et en cliquant sur « Soumettre », vous acceptez de recevoir des communications SMS de Blackfire Innovation. Vous pouvez vous désabonner à tout moment en répondant ARRÊT. Pour plus d'informations, veuillez consulter notre",
        "smsOnlyForPhone": "Les communications SMS ne sont disponibles que pour les numéros de téléphone.",
        "privacyPolicy": "Politique de confidentialité",
        "submit": "Soumettre",
        "claimPrize": "Réclamer le prix",
        "areYouReady": "Êtes-vous prêt à gagner?",
        "cameraPermissionIntro": "Pour profiter de cette expérience de réalité augmentée, veuillez autoriser l'accès à la caméra sur l'écran suivant.",
        "ready": "Prêt",
        "termsAcknowledgment": "En cliquant sur « Prêt », vous reconnaissez que vous avez plus de 21 ans et acceptez les",
        "termsOfService": "Conditions d'utilisation",
        "and": "et la",
        "ofBlackfire": "de Blackfire Innovation, Inc.",
        "spin": "Tourner",
        "keepCodeInFrame": "Veuillez garder le code dans le cadre",
        "rotateDevice": "Veuillez faire pivoter votre appareil en mode portrait",
        "checkingCameraPermissions": "Vérification des autorisations de caméra...",
        "allowCameraPermissions": "Veuillez autoriser l'accès à la caméra.",
        "unableToAccessCamera": "Impossible d'accéder à votre caméra.",
        "cameraDeniedPermissions": "Vous avez refusé l'accès à la caméra.",
        "unableToStartCamera": "Impossible de démarrer votre caméra.",
        "proceedToLoyaltyDesk": "Veuillez vous rendre au comptoir de fidélisation pour réclamer votre prix.",
        "loyaltyRepresentative": "Une fois devant un représentant de fidélisation, appuyez sur le bouton Réclamer pour recevoir votre prix.",
        "redeem": "Réclamer",
        "prizeWon": "Prix gagné:",
        "redemptionCode": "Code de réclamation:",
        "thankYouForPlaying": "Merci d'avoir joué!",
        "loading": "Chargement...",
        "error": "Erreur",
        "tryAgain": "Réessayer"
      },
      "campaignError": {
        "invalidLink": "Lien invalide",
        "campaignNotFound": "Campagne non trouvée",
        "campaignExpired": "Campagne expirée",
        "campaignNotStarted": "Campagne non commencée",
        "campaignUnavailable": "Campagne non disponible",
        "securityError": "Erreur de sécurité",
        "somethingWentWrong": "Quelque chose s'est mal passé",
        "campaignCode": "Code de campagne:",
        "whatYouCanDo": "Ce que vous pouvez faire:",
        "goToHomepage": "Aller à la page d'accueil",
        "contactSupport": "Contacter le support",
        "needImmediateAssistance": "Besoin d'aide immédiate? Contactez le lieu directement ou scannez un nouveau code QR.",
        "clientMismatchMessage": "Cette campagne appartient à un autre client ({{foundClient}}) et ne peut être accédée depuis cette instance d'application (configurée pour {{expectedClient}}). Il s'agit d'une protection de sécurité pour empêcher l'accès croisé aux données client.",
        "suggestedActions": {
          "contactVenue": "Contactez le lieu qui a fourni ce code QR",
          "verifyCorrectCode": "Vérifiez que vous scannez le bon code QR pour cet emplacement",
          "ownerContactSupport": "Si vous êtes le propriétaire du lieu, contactez le support pour l'aide à la configuration"
        }
      },
      "common": {
        "loading": "Chargement...",
        "error": "Erreur",
        "success": "Succès",
        "close": "Fermer",
        "cancel": "Annuler",
        "ok": "OK",
        "yes": "Oui",
        "no": "Non"
      }
    };
  }

  /**
   * Initialize the default locale
   */
  private async initializeDefaultLocale(): Promise<void> {
    try {
      await this.loadTranslations('en-us');
    } catch (error) {
      console.error('Failed to initialize default locale', error);
    }
  }

  /**
   * Get nested value from object using dot notation
   */
  private getNestedValue(obj: any, path: string): string | undefined {
    return path.split('.').reduce((current, key) => {
      return current && current[key] !== undefined ? current[key] : undefined;
    }, obj);
  }

  /**
   * Interpolate parameters in translation strings
   */
  private interpolateParams(translation: string, params?: { [key: string]: any }): string {
    if (!params) {
      return translation;
    }

    return translation.replace(/\{\{(\w+)\}\}/g, (match, key) => {
      return params[key] !== undefined ? params[key] : match;
    });
  }
}